/**
 * Get display name for filter types used in result views
 */
export const getFilterDisplayName = (filterBy: string): string => {
    switch (filterBy) {
        case 'all':
            return 'All Results';
        case 'normal':
        case 'within-range':
            return 'Within Range';
        case 'abnormal':
            return 'Abnormal Results';
        case 'borderline':
        case 'needs-attention':
            return 'Needs Attention';
        case 'critical':
        case 'high-priority':
            return 'High Priority';
        case 'baseline':
            return 'Baseline Health';
        case 'thyroid':
            return 'Thyroid Health';
        case 'allergy':
            return 'Allergy Tests';
        default:
            return 'Selected Filter';
    }
};

/**
 * Generate mock historical data for biomarker trend visualization
 */
export const generateMockHistoricalData = (currentValue: number) => {
    const baseValue = parseFloat(String(currentValue));
    if (isNaN(baseValue)) return [];

    const roundToOne = (num: number) => Math.round(num * 10) / 10;

    return [
        {date: 'Dec 24', value: roundToOne(baseValue * 0.85), label: "Dec '24"},
        {date: 'Feb 25', value: roundToOne(baseValue * 0.92), label: "Feb '25"},
        {date: 'Jun 25', value: roundToOne(baseValue * 0.88), label: "Jun '25"},
        {date: 'Jul 25', value: roundToOne(baseValue * 1.02), label: "Jul '25"},
    ];
};

/**
 * Collect all results for a specific test name from patient data
 */
export const collectTestResults = (allPatientData: any[], testName: string) => {
    const allResults: any[] = [];

    // Normalize the search test name for case-insensitive matching
    const normalizedSearchName = testName.toLowerCase().trim();

    allPatientData.forEach((test: any) => {
        if (test.results) {
            test.results.forEach((result: any) => {
                // Normalize result test name for comparison
                const resultTestName = result.test_name || '';
                const normalizedResultName = resultTestName.toLowerCase().trim();

                // Check for exact match (case-insensitive)
                const isExactMatch = normalizedResultName === normalizedSearchName;

                // Check for compound test name match (e.g., "Panel Name; Test Name")
                const isCompoundMatch =
                    resultTestName.includes('; ') &&
                    resultTestName.split('; ')[1]?.toLowerCase().trim() === normalizedSearchName;

                if (isExactMatch || isCompoundMatch) {
                    allResults.push({
                        ...result,
                        amendedDate: test.amendedDate,
                        createdDate: test.createdDate,
                    });
                }
            });
        }
    });

    return allResults;
};

/**
 * Get historical data for a biomarker
 * Returns real data for subscribers with historical tests, mock data otherwise
 */
export const getHistoricalData = (
    testName: string,
    currentValue: number,
    subscription: boolean,
    allPatientData: any[] | null,
    excludeMostRecent: boolean = false
) => {
    if (subscription && allPatientData && allPatientData.length > 0) {
        const allResults = collectTestResults(allPatientData, testName);

        // Show history if we have at least 1 historical data point
        // (The BiomarkerChartCard will add the current value, so 1 historical + current = 2 points on chart)
        if (allResults.length >= 1) {
            const sortedResults = allResults.sort((a, b) => {
                // Sort by result statusDate first (most accurate for biomarker timing),
                // fallback to test amendedDate, then createdDate
                const aDate = new Date(a.statusDate || a.amendedDate || a.createdDate).getTime();
                const bDate = new Date(b.statusDate || b.amendedDate || b.createdDate).getTime();
                return aDate - bDate;
            });

            // Exclude the most recent result if requested (it will be shown as "Current")
            // BUT: Keep it if it's the only result we have (need at least 1 point for history)
            const shouldExclude = excludeMostRecent && sortedResults.length > 1;
            const resultsToUse = shouldExclude ? sortedResults.slice(0, -1) : sortedResults;

            const realHistoricalData = resultsToUse.map((result) => ({
                date: new Date(result.statusDate || result.createdDate).toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit',
                }),
                value: parseFloat(result.result) || 0,
                label: new Date(result.statusDate || result.createdDate).toLocaleDateString('en-US', {
                    month: 'short',
                    year: '2-digit',
                }),
            }));

            return realHistoricalData;
        }
    }

    // Fall back to mock data for non-subscribers or when no test data
    return generateMockHistoricalData(currentValue);
};
